import { useNotification } from '@gohighlevel/ghl-ui'

export interface ToastNotificationOptions {
  title: string
  content?: string
  duration?: number
  type?: 'success' | 'error' | 'warning' | 'info'
}

/**
 * Toast notification helper that provides common notification patterns
 * with consistent styling and durations throughout the application
 */
export function useToastNotification() {
  const notification = useNotification()

  /**
   * Show a success notification
   * @param options - Notification options
   */
  const showSuccess = (options: ToastNotificationOptions | string) => {
    const config =
      typeof options === 'string'
        ? { title: options, type: 'success' as const, duration: 3000 }
        : { type: 'success' as const, duration: 3000, ...options }

    return notification.success(config)
  }

  /**
   * Show an error notification
   * @param options - Notification options
   */
  const showError = (options: ToastNotificationOptions | string) => {
    const config =
      typeof options === 'string'
        ? { title: options, type: 'error' as const, duration: 4000 }
        : { type: 'error' as const, duration: 4000, ...options }

    return notification.create(config)
  }

  /**
   * Show a warning notification
   * @param options - Notification options
   */
  const showWarning = (options: ToastNotificationOptions | string) => {
    const config =
      typeof options === 'string'
        ? { title: options, type: 'warning' as const, duration: 3000 }
        : { type: 'warning' as const, duration: 3000, ...options }

    return notification.create(config)
  }

  /**
   * Show an info notification
   * @param options - Notification options
   */
  const showInfo = (options: ToastNotificationOptions | string) => {
    const config =
      typeof options === 'string'
        ? { title: options, type: 'info' as const, duration: 3000 }
        : { type: 'info' as const, duration: 3000, ...options }

    return notification.create(config)
  }

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,

    // Direct access to the underlying notification instance
    notification,
  }
}
